import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { formatNumber, formatRelativeTime, getGreeting } from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import {
	AlertCircle,
	ArrowUpRight,
	Brain,
	CheckCircle2,
	Clock,
	DollarSign,
	FileText,
	Network,
	RefreshCw,
	Search,
	Sparkles,
	TrendingUp,
	Upload,
	Zap,
} from "lucide-react";
import {
	Area,
	AreaChart,
	CartesianGrid,
	Tooltip as RechartsTooltip,
	ResponsiveContainer,
	XAxis,
	YAxis,
} from "recharts";

/** Map DB event types to icons */
const eventIcons: Record<string, React.ReactNode> = {
	document_ingested: <RefreshCw size={14} className="text-info" />,
	proposals_generated: <Sparkles size={14} className="text-warning" />,
	candidate_approved: <CheckCircle2 size={14} className="text-success" />,
	candidate_rejected: <span className="text-destructive text-sm">&times;</span>,
	highlights_published: <Zap size={14} className="text-primary" />,
	index_upserted: <Brain size={14} className="text-purple-400" />,
	question_asked: <Search size={14} className="text-muted-foreground" />,
	answer_generated: <Sparkles size={14} className="text-cyan-400" />,
	graph_canonicalization_needed: <Network size={14} className="text-warning" />,
	graph_canonicalization_resolved: <Network size={14} className="text-success" />,
};

export function DashboardPage() {
	const navigate = useNavigate();

	// Real API data
	const ingestStats = trpc.ingest.stats.useQuery();
	const indexStats = trpc.index.stats.useQuery();
	const feedbackAnalytics = trpc.feedback.analytics.useQuery({});

	// Events: activity feed + daily chart
	const recentEvents = trpc.events.recent.useQuery({ limit: 10 });
	const dailyCounts = trpc.events.dailyCounts.useQuery({ days: 7 });

	const syncMutation = trpc.ingest.triggerReaderSync.useMutation({
		onSuccess: () => {
			// Refresh all dashboard data after sync
			ingestStats.refetch();
			recentEvents.refetch();
			dailyCounts.refetch();
		},
	});

	// Derive dashboard values from API data
	const totalDocuments = ingestStats.data?.total ?? 0;
	const pendingReview = ingestStats.data?.counts?.["awaiting_approval"] ?? 0;
	const queuedCount = ingestStats.data?.counts?.["queued"] ?? 0;

	const totalConcepts = indexStats.data?.graphNodeCount ?? 0;
	const totalEdges = indexStats.data?.graphEdgeCount ?? 0;
	const vectorCount = indexStats.data?.vectorPointCount ?? 0;

	const totalRejections = feedbackAnalytics.data?.totalRejections ?? 0;

	// Transform daily event counts for chart
	const trendChart =
		dailyCounts.data?.map((d) => ({
			date: new Date(d.date).toLocaleDateString("en-US", {
				weekday: "short",
			}),
			value: d.count,
		})) ?? [];

	const isLoading = ingestStats.isLoading || indexStats.isLoading;

	return (
		<div className="p-6 max-w-[1400px] mx-auto space-y-6 animate-fade-in">
			{/* Welcome section */}
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-2xl font-display font-bold tracking-tight">{getGreeting()}</h2>
					<p className="text-sm text-muted-foreground mt-1">
						{isLoading ? (
							<Skeleton className="h-4 w-60 inline-block" />
						) : (
							<>
								You have{" "}
								<span className="text-primary font-semibold">
									{pendingReview + queuedCount} documents
								</span>{" "}
								waiting for review.
							</>
						)}
					</p>
				</div>
				<div className="flex items-center gap-2">
					<Button
						variant="outline"
						size="sm"
						className="gap-2"
						onClick={() =>
							syncMutation.mutate({
								locations: ["new", "later"],
								mode: "manual_tag",
								withHtmlContent: true,
							})
						}
						disabled={syncMutation.isPending}
					>
						<RefreshCw size={14} className={syncMutation.isPending ? "animate-spin" : ""} />
						{syncMutation.isPending ? "Queueing..." : "Sync Now"}
					</Button>
					<Button
						variant="outline"
						size="sm"
						className="gap-2"
						onClick={() => navigate({ to: "/ingest" })}
					>
						<Upload size={14} />
						Ingest
					</Button>
					<Button size="sm" className="gap-2" onClick={() => navigate({ to: "/review" })}>
						<Sparkles size={14} />
						Review Next
					</Button>
				</div>
			</div>

			{/* Stats Cards */}
			<div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
				{isLoading ? (
					[
						"dashboard-stat-skeleton-1",
						"dashboard-stat-skeleton-2",
						"dashboard-stat-skeleton-3",
						"dashboard-stat-skeleton-4",
					].map((skeletonId) => (
						<Card key={skeletonId}>
							<CardContent className="p-4">
								<Skeleton className="h-4 w-24 mb-3" />
								<Skeleton className="h-8 w-16" />
							</CardContent>
						</Card>
					))
				) : (
					<>
						<StatCard
							label="Total Documents"
							value={formatNumber(totalDocuments)}
							icon={<FileText size={16} />}
							trend={`${queuedCount} queued`}
						/>
						<StatCard
							label="Pending Review"
							value={pendingReview.toString()}
							icon={<Clock size={16} />}
							trend={`${queuedCount} in queue`}
							accent
						/>
						<StatCard
							label="Rejections"
							value={totalRejections.toString()}
							icon={<CheckCircle2 size={16} />}
							trend={
								feedbackAnalytics.data
									? `across ${feedbackAnalytics.data.byCategory?.length ?? 0} categories`
									: "Loading..."
							}
						/>
						<StatCard
							label="Vectors Indexed"
							value={formatNumber(vectorCount)}
							icon={<DollarSign size={16} />}
							trend={`${formatNumber(totalConcepts)} concepts`}
						/>
					</>
				)}
			</div>

			{/* Main Content Grid */}
			<div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
				{/* Processing Trend Chart */}
				<Card className="lg:col-span-3">
					<CardHeader className="pb-2">
						<div className="flex items-center justify-between">
							<CardTitle className="text-sm">Processing Activity</CardTitle>
							<Badge variant="secondary" className="text-[10px]">
								{dailyCounts.data ? "Live" : "Loading"}
							</Badge>
						</div>
					</CardHeader>
					<CardContent className="pt-0">
						<div className="h-[200px] w-full">
							<ResponsiveContainer width="100%" height="100%">
								<AreaChart data={trendChart}>
									<defs>
										<linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
											<stop offset="0%" stopColor="#d42a2a" stopOpacity={0.3} />
											<stop offset="100%" stopColor="#d42a2a" stopOpacity={0} />
										</linearGradient>
									</defs>
									<CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
									<XAxis
										dataKey="date"
										axisLine={false}
										tickLine={false}
										tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
									/>
									<YAxis
										axisLine={false}
										tickLine={false}
										tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
										width={30}
									/>
									<RechartsTooltip
										contentStyle={{
											background: "var(--popover)",
											border: "1px solid var(--border)",
											borderRadius: "8px",
											fontSize: "12px",
											color: "var(--foreground)",
										}}
									/>
									<Area
										type="monotone"
										dataKey="value"
										stroke="#d42a2a"
										strokeWidth={2}
										fill="url(#chartGradient)"
										dot={{ fill: "#d42a2a", r: 3, strokeWidth: 0 }}
										activeDot={{
											r: 5,
											fill: "#d42a2a",
											strokeWidth: 2,
											stroke: "#fff",
										}}
									/>
								</AreaChart>
							</ResponsiveContainer>
						</div>
					</CardContent>
				</Card>

				{/* Activity Feed — real events from API */}
				<Card className="lg:col-span-2">
					<CardHeader className="pb-2">
						<div className="flex items-center justify-between">
							<CardTitle className="text-sm">Recent Activity</CardTitle>
							<Badge variant="secondary" className="text-[10px]">
								{recentEvents.data ? `${recentEvents.data.total} total` : "Loading"}
							</Badge>
						</div>
					</CardHeader>
					<CardContent className="pt-0">
						<ScrollArea className="h-[200px]">
							{recentEvents.isLoading ? (
								<div className="space-y-3 py-2">
									{[
										"dashboard-event-skeleton-1",
										"dashboard-event-skeleton-2",
										"dashboard-event-skeleton-3",
										"dashboard-event-skeleton-4",
									].map((skeletonId) => (
										<div key={skeletonId} className="flex items-start gap-3">
											<Skeleton className="h-6 w-6 rounded-md shrink-0" />
											<div className="flex-1">
												<Skeleton className="h-3 w-32 mb-1" />
												<Skeleton className="h-3 w-48" />
											</div>
										</div>
									))}
								</div>
							) : recentEvents.data && recentEvents.data.items.length > 0 ? (
								<div className="space-y-0">
									{recentEvents.data.items.map((event, i) => (
										<div
											key={event.id}
											className="flex items-start gap-3 py-2.5 border-b border-border/30 last:border-0"
											style={{ animationDelay: `${i * 50}ms` }}
										>
											<div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-md bg-muted/50 shrink-0">
												{eventIcons[event.type] ?? (
													<Zap size={14} className="text-muted-foreground" />
												)}
											</div>
											<div className="flex-1 min-w-0">
												<p className="text-xs font-medium text-foreground truncate">
													{event.title}
												</p>
												<p className="text-[11px] text-muted-foreground truncate">
													{event.description}
												</p>
											</div>
											<span className="text-[10px] text-muted-foreground/60 shrink-0 mt-0.5">
												{formatRelativeTime(event.createdAt)}
											</span>
										</div>
									))}
								</div>
							) : (
								<div className="flex flex-col items-center justify-center py-8 text-center">
									<p className="text-xs text-muted-foreground">No activity yet</p>
									<p className="text-[10px] text-muted-foreground/50 mt-1">
										Sync documents or run a search to generate events
									</p>
								</div>
							)}
						</ScrollArea>
					</CardContent>
				</Card>
			</div>

			{/* Sync result banner */}
			{syncMutation.isSuccess && syncMutation.data && (
				<Card className="border-success/20 bg-success/[0.03] animate-slide-up">
					<CardContent className="py-3 flex items-center gap-3">
						<CheckCircle2 size={16} className="text-success" />
						<span className="text-sm">
							Reader sync queued: {syncMutation.data.locations.join(" + ")} in manual tag mode
						</span>
					</CardContent>
				</Card>
			)}

			{syncMutation.isError && (
				<Card className="border-destructive/20 bg-destructive/[0.03] animate-slide-up">
					<CardContent className="py-3 flex items-center gap-3">
						<AlertCircle size={16} className="text-destructive" />
						<span className="text-sm text-destructive">
							Sync failed: {syncMutation.error.message}
						</span>
					</CardContent>
				</Card>
			)}

			{/* Knowledge Graph Health */}
			<Card>
				<CardContent className="py-4">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-6">
							<div className="flex items-center gap-2">
								<Network size={16} className="text-primary" />
								<span className="text-sm font-display font-semibold">Knowledge Graph</span>
							</div>
							{isLoading ? (
								<Skeleton className="h-4 w-60" />
							) : (
								<div className="flex items-center gap-6 text-sm">
									<div>
										<span className="text-muted-foreground">Concepts</span>{" "}
										<span className="font-mono font-semibold text-foreground">
											{formatNumber(totalConcepts)}
										</span>
									</div>
									<div>
										<span className="text-muted-foreground">Edges</span>{" "}
										<span className="font-mono font-semibold text-foreground">
											{formatNumber(totalEdges)}
										</span>
									</div>
									<div>
										<span className="text-muted-foreground">Vectors</span>{" "}
										<span className="font-mono font-semibold text-foreground">
											{formatNumber(vectorCount)}
										</span>
									</div>
								</div>
							)}
						</div>
						<Button
							variant="ghost"
							size="sm"
							className="gap-1.5 text-muted-foreground text-xs"
							onClick={() => navigate({ to: "/graph" })}
						>
							Explore
							<ArrowUpRight size={12} />
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

function StatCard({
	label,
	value,
	icon,
	trend,
	trendUp,
	accent,
}: {
	label: string;
	value: string;
	icon: React.ReactNode;
	trend: string;
	trendUp?: boolean;
	accent?: boolean;
}) {
	return (
		<Card className={accent ? "border-primary/20 bg-primary/[0.03]" : ""}>
			<CardContent className="p-4">
				<div className="flex items-center justify-between mb-3">
					<span className="text-xs text-muted-foreground font-medium">{label}</span>
					<div className="flex h-7 w-7 items-center justify-center rounded-md bg-muted/50 text-muted-foreground">
						{icon}
					</div>
				</div>
				<div className="flex items-end justify-between">
					<span
						className={`text-2xl font-display font-bold tracking-tight ${
							accent ? "text-primary" : ""
						}`}
					>
						{value}
					</span>
					<span
						className={`text-[11px] font-medium ${
							trendUp ? "text-success" : "text-muted-foreground"
						}`}
					>
						{trendUp && <TrendingUp size={10} className="inline mr-0.5" />}
						{trend}
					</span>
				</div>
			</CardContent>
		</Card>
	);
}
